import {ColumnDef} from "@tanstack/react-table";
import {sortableColumnHeader} from "@/components/data-table.tsx";
import {useMembersTranslation} from "@/i18n/hooks";
import {Link} from "@/components/tailwind/link.tsx";
import {Text} from "@/components/tailwind/text"
import {SquareUserRoundIcon} from "lucide-react";
import {ExclamationTriangleIcon} from "@heroicons/react/16/solid";
import {MemberListItem, PhoneNumber, SimplePhoneNumber} from "@/models/member.ts";
import PhoneNumberDisplay from "@/components/phone-number-display.tsx";
import {useMemo} from "react";
import {useLocation} from "react-router-dom";

function NameCell({ member }: { member: MemberListItem }) {
    const {t} = useMembersTranslation();
    const nameContent = [member.salutation, member.title, member.firstName, member.lastName].filter(Boolean).join(" ");

    if (!member.companyName) {
        return (
            <div className="flex items-center gap-2">
                {member.missingConfidentiality && (
                    <ExclamationTriangleIcon
                        className="h-4 w-4 text-amber-500 dark:text-amber-400 flex-shrink-0"
                        title={t('memberList.missingConfidentialityTitle')}
                    />
                )}
                <Text>{nameContent}</Text>
            </div>
        );
    }

    return (
        <div className="flex items-start gap-2">
            {member.missingConfidentiality && (
                <ExclamationTriangleIcon
                    className="h-4 w-4 text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5"
                    title={t('memberList.missingConfidentialityTitle')}
                />
            )}
            <span>
                <Text>{nameContent}</Text>
                {member.companyName && (
                    <Text>{t('memberList.companyPrefix')} {member.companyName}</Text>
                )}
            </span>
        </div>
    );
}

function StatusCell({ status }: { status: string }) {
    const {t} = useMembersTranslation();
    const testId = status === 'ACTIVE_MEMBER' ? 'member-status-active' : 'member-status-passive';
    return (
        <span data-testid={testId}>
            {t(`memberList.table.statusColumn.${status}`)}
        </span>
    );
}

function generatePhoneColumnContent(phoneNumbers?: PhoneNumber[] | SimplePhoneNumber[]) {
    if (!Array.isArray(phoneNumbers) || !phoneNumbers?.length) {
        return (<></>)
    }

    return (
        <section role="list">
            {phoneNumbers.map((entry, index) => (
                    <PhoneNumberDisplay type={entry.type} number={entry.number} key={index}/>
                )
            )}
        </section>
    )
}

function constructSortableNameValue(row: MemberListItem) {
    return `${row.lastName || ''}, ${row.firstName || ''} ${row.title || ''} ${row.salutation || ''} ` +
        `${row.companyName || ''}`.trim();
}

export const memberColumns: ColumnDef<MemberListItem>[] = [
    {
        accessorKey: "memberNo",
        header: ({column}) =>
            sortableColumnHeader(column, "memberList.table.headers.memberNo", "members")
    },
    {
        id: "name",
        accessorFn: constructSortableNameValue,
        header: ({column}) =>
            sortableColumnHeader(column, "memberList.table.headers.name", "members"),
        cell: ({row}) => <NameCell member={row.original} />,

    },
    {
        accessorKey: "address",
        header: ({column}) => sortableColumnHeader(column, "memberList.table.headers.address", "members"),
        cell: ({row}) => {
            return (
                <address>
                    <Text>{row.original.street} {row.original.houseNumber}</Text>
                    <Text>{row.original.postcode} {row.original.city}</Text>
                </address>
            )
        }

    },
    {
        id: "phoneNumbers",
        header: ({column}) => sortableColumnHeader(column, "memberList.table.headers.phoneNumbers", "members"),
        cell: ({row}) => generatePhoneColumnContent(row.original.phoneNumbers)
    },
    {
        accessorKey: "membershipStatus",
        accessorFn: (row) => `${row.status}`.trim(),
        header:
            ({column}) =>
                sortableColumnHeader(column, "memberList.table.headers.status", "members"),
        cell: ({row}) => <StatusCell status={row.original.status} />
    },
    {
        id: "actions",
        cell: ({row}) => {
            const member = row.original

            return (
                <Link 
                    href={`/members/${member.id}`} 
                    data-testid={`member-details-link-${member.id}`}
                    aria-label={`View details for member ${member.memberNo}`}
                >
                    <SquareUserRoundIcon/>
                </Link>

            )
        }

    }
]

export function useMemberColumns(): ColumnDef<MemberListItem>[] {
    const location = useLocation();

    return useMemo(() => {
        const returnUrl = location.search
            ? encodeURIComponent(`/members${location.search}`)
            : '';

        return [
            {
                accessorKey: "memberNo",
                header: ({column}) =>
                    sortableColumnHeader(column, "memberList.table.headers.memberNo", "members")
            },
            {
                id: "name",
                accessorFn: constructSortableNameValue,
                header: ({column}) =>
                    sortableColumnHeader(column, "memberList.table.headers.name", "members"),
                cell: ({row}) => <NameCell member={row.original} />,
            },
            {
                accessorKey: "address",
                header: ({column}) => sortableColumnHeader(column, "memberList.table.headers.address", "members"),
                cell: ({row}) => {
                    return (
                        <address>
                            <Text>{row.original.street} {row.original.houseNumber}</Text>
                            <Text>{row.original.postcode} {row.original.city}</Text>
                        </address>
                    )
                }
            },
            {
                id: "phoneNumbers",
                header: ({column}) => sortableColumnHeader(column, "memberList.table.headers.phoneNumbers", "members"),
                cell: ({row}) => generatePhoneColumnContent(row.original.phoneNumbers)
            },
            {
                accessorKey: "membershipStatus",
                accessorFn: (row) => `${row.status}`.trim(),
                header: ({column}) =>
                    sortableColumnHeader(column, "memberList.table.headers.status", "members"),
                cell: ({row}) => <StatusCell status={row.original.status} />
            },
            {
                id: "actions",
                cell: ({row}) => {
                    const member = row.original
                    const href = returnUrl
                        ? `/members/${member.id}?returnUrl=${returnUrl}`
                        : `/members/${member.id}`

                    return (
                        <Link 
                            href={href} 
                            data-testid={`member-details-link-${member.id}`}
                            aria-label={`View details for member ${member.memberNo}`}
                        >
                            <SquareUserRoundIcon/>
                        </Link>
                    )
                }
            }
        ];
    }, [location.search]);
}
